import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationApiModels } from '../../../../models/api/authentication-api.models';
import { AuthenticationModels } from '../../../../models/authentication.models';
import { CommonModels } from '../../../../models/common.models';
import { AppInsightsService } from '../../app-insights/app-insights.service';
import { AuthenticationApiService } from './api/authentication-api.service';
import { ILogger } from './logger/ilogger';
import { LoggerService } from './logger/logger.service';
import { LocalStorageService } from './storage/local-storage.service';

const ACCESS_TOKEN = 'ACCESS_TOKEN';
const REFRESH_TOKEN = 'REFRESH_TOKEN';

export interface IAuthenticationService {
  getAccessToken(): string;
}

@Injectable({
  providedIn: 'root',
})
export abstract class AuthenticationServiceBase<
  TModel extends AuthenticationModels.Account,
  TApiModel extends AuthenticationApiModels.AccountInfoResponse,
> implements IAuthenticationService
{
  private accessToken!: string;
  private refreshToken!: string;

  protected _user: TModel | null | undefined;

  private userSubject = new BehaviorSubject<TModel | null>(null);
  public $user = this.userSubject.asObservable();
  protected logger: ILogger;
  protected abstract accountKind: CommonModels.AccountKind;
  protected abstract accountProjector(TApiModel: any): TModel;

  constructor(
    protected authApiService: AuthenticationApiService,
    private storageService: LocalStorageService,
    private appInsightService: AppInsightsService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.getLogger('Auth Service');
  }

  login(login: string, password: string): Observable<void> {
    return this.authApiService.login(login, password, this.accountKind).pipe(
      map((apiAcc: AuthenticationApiModels.TokensResponse) => {
        this.setAccessToken(apiAcc.accessToken!);
        this.setRefreshToken(apiAcc.refreshToken!);
        return;
      }),
    );
  }

  loginTunnel(id: number): Observable<boolean> {
    return this.authApiService.loginTunnel(id).pipe(
      map((apiAcc: AuthenticationApiModels.TokensResponse) => {
        this.setAccessToken(apiAcc.accessToken!);
        this.setRefreshToken(apiAcc.refreshToken!);
        return true;
      }),
    );
  }

  impersonate(tokens: AuthenticationApiModels.TokensResponse) {
    this.setAccessToken(tokens.accessToken!);
    this.setRefreshToken(tokens.refreshToken!);
  }

  canEditPolicy() {
    return this.hasRight(AuthenticationModels.RightEnum.EditPolicy);
  }

  logoff(): Observable<boolean> {
    return this.authApiService.logoff().pipe(
      map(() => {
        this.setUser(undefined);
        this.clearTokens();
        return true;
      }),
    );
  }

  protected setUser(user: TModel | null | undefined) {
    this._user = user;
    if (user) {
      this.appInsightService.setUserContext(user.id.toString());
    } else {
      this.appInsightService.clearUserContext();
    }

    this.notifyNewUser(user);
  }

  lostPassword(email: string): Observable<void> {
    return this.authApiService.lostPassword(email);
  }

  resetPassword(password: string, token: string): Observable<void> {
    return this.authApiService.resetPassword(password, token);
  }

  renewToken(): Observable<string> {
    return this.authApiService.renewToken(this.getRefreshToken()).pipe(
      map((resp) => {
        if (!resp.token) {
          throw new Error('Token is undefined');
        }
        this.setAccessToken(resp.token);
        return resp.token;
      }),
    );
  }

  /**
   * Get the current user
   * @param force Force the API call
   */
  getUser(force: boolean = false): Observable<TModel> {
    // If force or no local user, call the api
    if (force || !this._user) {
      return this.authApiService.me().pipe(
        map((apiAcc: AuthenticationApiModels.AccountInfoResponse) => {
          let acc = this.accountProjector(apiAcc as TApiModel);
          this.setUser(acc);
          return acc;
        }),
      );
    } else {
      // Otherwise use the localUser in memory
      return of(this._user);
    }
  }

  public notifyNewUser(user: TModel | null | undefined) {
    this.userSubject.next(user ?? null);
  }

  hasRight(right: number): boolean {
    if (!this._user) {
      return false;
    }

    return this._user.hasRight(right);
  }

  isAuto(): boolean {
    return this._user?.backOfficeAuto ?? false;
  }

  isSante(): boolean {
    return this._user?.backOfficeSante ?? false;
  }

  public getAccessToken(): string {
    if (this.accessToken === undefined) {
      this.accessToken = this.storageService.getString(ACCESS_TOKEN);
    }

    return this.accessToken;
  }

  public isAdminImpersonated(): boolean {
    let tokenInfo: any = jwtDecode(this.getAccessToken()); // decode token
    return Boolean(tokenInfo['ImpersonatorAccountIsAdmin']);
  }

  public getRefreshToken(): string {
    if (this.refreshToken === undefined) {
      this.refreshToken = this.storageService.getString(REFRESH_TOKEN);
    }

    return this.refreshToken;
  }
  private clearTokens() {
    this.storageService.clearItem(ACCESS_TOKEN);
    this.storageService.clearItem(REFRESH_TOKEN);
    this.accessToken = '';
    this.refreshToken = '';
  }

  protected setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
    this.storageService.setString(ACCESS_TOKEN, accessToken);
  }

  protected setRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
    this.storageService.setString(REFRESH_TOKEN, refreshToken);
  }
}
